import { useEffect, useState } from 'react';
import { Drawer, Portal, CloseButton, Center, Spinner } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import ViewCollection from './view-collection';
import { baseApi } from '@/utils/api';
import { Collection } from '@/lib/types';

interface Props {
  isOpen: boolean;
  onClose(): void;
  referenceId: string;
}

interface Item {
  collection: Collection;
  ContentWithStructure: {
    groups: {
      name: string;
      contents: string[];
      ContentValues: {
        reference_id: string;
        name: string;
        description: string;
        content_type_name: string;
      }[];
    }[];
  };
  User: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export default function CollectionDrawer({ isOpen, onClose, referenceId }: Props) {
  const [item, setItem] = useState<Item | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getCollection = async () => {
      setItem(null);
      setIsLoading(true);

      try {
        const response = await baseApi.get(`public/collections/reference/${referenceId}`);
        const item = await response.data;
        if (!item) {
          return;
        }
        setItem(item);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to load collection',
        });
      } finally {
        setIsLoading(false);
      }
    };

    getCollection();
  }, [referenceId]);

  return (
    <Drawer.Root
      id={`view-collection-drawer-${referenceId}`}
      size='xl'
      placement='end'
      open={isOpen}
      onOpenChange={onClose}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton size='sm' />
            </Drawer.CloseTrigger>
            <Drawer.Body>
              {isLoading && (
                <Center py={10}>
                  <Spinner size='xl' />
                </Center>
              )}
              {item && <ViewCollection item={item} isPublic={false} />}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
